import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { IdentificationContent } from '../models/identification-content.model';
import { Tenancy } from '../models/tenancy.model';
import { ErrorMessage } from '../models/error-message.model';
import { Answers } from '../models/answers.model';

/**
 * View Model Service for Identification & Verification Wizard
 *
 * This service acts as a centralized state store for the entire identification wizard.
 * It manages:
 * - CMS content (loaded from Umbraco)
 * - Wizard state across all 4 steps
 * - Error handling
 * - Observable notifications when content is ready
 */
@Injectable({
    providedIn: 'root'
})
export class ViewModelService {
    // Step 1 properties
    tenancyReference = '';
    postcode = '';
    tenancy: Tenancy = {
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        postcode: '',
        assetId: '',
        allowPortalRegistration: false
    };

    // Step 2 properties
    selectedContactId: string | null = null;

    answers = new Answers();

    // Step 3 confirm properties
    resumeUrl: string = '';
    loginUrl: string = '';

    // Common properties
    content: IdentificationContent | null = null;
    error: ErrorMessage | null = null;

    // Observable for content loading
    private contentSource = new BehaviorSubject<boolean>(false);
    public contentLoaded: Observable<boolean> = this.contentSource.asObservable();
    public isReady = false;

    private http = inject(HttpClient);

    constructor() {
        this.loadContent();
    }

    /**
     * Loads CMS content from Umbraco API
     * This content includes all text, labels, error messages, and modal content for all wizard steps
     *
     * API: GET /umbraco/api/identification/get
     * Returns: IdentificationContent object with all text content
     */
    private loadContent(): void {
        const umbracoUrl = `https://${window.location.host}/umbraco/api/identification/get`;

        this.http.get<IdentificationContent>(umbracoUrl).subscribe({
            next: (response) => {
                this.content = response;
                this.isReady = true;
                this.contentSource.next(true);
                console.log('Identification content loaded from Umbraco');
            },
            error: (error: HttpErrorResponse) => {
                console.error('Failed to load identification content from Umbraco:', error);
                // Set default content to prevent app from breaking
                this.setDefaultContent();
                this.isReady = true;
                this.contentSource.next(true);
            }
        });
    }

    /**
     * Fallback content in case Umbraco API is unavailable
     * This ensures the app can still function with basic text
     */
    private setDefaultContent(): void {
        this.content = {
            title: 'Identity & Verification',
            step1Title: 'Getting Started',
            step1Summary: 'Please provide your tenancy details to begin the verification process.',
            step1SubTitle: 'Find Your Tenancy',
            step1Question: '',
            step1SectionTitle: '',
            step1ProfileInformationMessageTitle: '',
            step1ProfileInformationMessageBody: '',
            step1ProfileInformationMessageButton: '',
            step1Body: '<p>To begin the identity and verification process, please enter your tenancy reference number and postcode below.</p>',
            step1TenancyReferenceNumberQuestion: 'Tenancy Reference Number',
            step1TenancyReferenceNumberError: 'Please enter a valid tenancy reference number (4-6 digits)',
            step1TenancyReferenceNumberLinkText: 'Where can I find this?',
            step1TenancyReferenceNumberModalTitle: 'Finding Your Tenancy Reference Number',
            step1TenancyReferenceNumberModalBody: '<p>Your tenancy reference number can be found on:</p><ul><li>Your tenancy agreement</li><li>Your rent statement</li><li>Any correspondence from your housing provider</li></ul><p>It is typically a 4-6 digit number.</p>',
            step1PostcodeQuestion: 'Postcode',
            step1PostcodeError: 'Please enter a valid UK postcode',
            step1PropertyNotFoundTitle: 'Property Not Found',
            step1PropertyNotFoundBody: 'We could not find a property matching the details you provided. Please check your tenancy reference and postcode and try again.',
            step1ConfirmTitle: '',
            step1ConfirmSubTitle: '',
            step1ConfirmContinueButton: '',
            step1ConfirmMyAddressButton: '',
            step1ConfirmBackButton: '',
            step1ConfirmProblemButton: '',
            step1InformationMessageTitle: 'Welcome to Identity & Verification',
            step1InformationMessageBody: '<p><strong>Important Information:</strong></p><p>This process will help us verify your identity and tenancy details. Please ensure you have the following information ready:</p><ul><li>Your tenancy reference number</li><li>Your current postcode</li><li>A valid form of identification</li></ul><p>The process should take approximately 10-15 minutes to complete.</p>',
            step1InformationMessageButton: 'I Understand',
            step2Title: 'Select Tenant',
            step2SubTitle: '',
            step2Question: '',
            step2Body: '',
            step2SectionTitle: '',
            step2NoTenantsTitle: '',
            step2NoTenantsDescription: '',
            step2TenantAlreadyRegistered: '',
            step2TenantBlocked: '',
            step3Title: 'Security Questions',
            step3SubTitle: '',
            step3Question: '',
            step3Body: '',
            step3InsufficientQuestionsErrorTitle: '',
            step3InsufficientQuestionsErrorDescription: '',
            step3InvalidSubmissionErrorTitle: '',
            step3InvalidSubmissionErrorDescription: '',
            step3TooManyAttemptsErrorTitle: '',
            step3TooManyAttemptsErrorDescription: '',
            step3TenancyStartDateQuestionText: '',
            step3DateOfBirthQuestionText: '',
            step3NationalInsuranceNumberQuestionText: '',
            step3NationalInsuranceNumberSupplementalText: '',
            step3TenancyStartDateModalTitle: '',
            step3TenancyStartDateModalBody: '',
            step3RentChargeQuestionText: '',
            step3RentChargeEmptyMessage: '',
            step3RentChargeModalTitle: '',
            step3RentChargeModalBody: '',
            step3InformationMessageTitle: '',
            step3InformationMessageBody: '',
            step3InformationMessageButton: '',
            step3ConfirmTitle: '',
            step3ConfirmWaitingTitle: '',
            step3ConfirmMessage: '',
            step3ConfirmTimeoutTitle: '',
            step3ConfirmTimeoutMessage: '',
            step3ConfirmTimeoutButtonText: '',
            stepAbortNotMyAddressTitle: '',
            stepAbortNotMyAddressSubTitle: '',
            stepAbortNotMyAddressMessage: '',
            stepAbortPortalNotAvailableTitle: '',
            stepAbortPortalNotAvailableSubTitle: '',
            stepAbortPortalNotAvailableMessage: '',
            stepAbortPortalNotAvailableMessageTitle: '',
            stepAbortUnknownErrorTitle: '',
            stepAbortUnknownErrorSubTitle: '',
            stepAbortUnknownErrorMessage: ''
        };
    }
}
